import React, { useEffect, useState } from "react"
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import { getRiderPendingOrdersCount } from "services/deliveryPartnerService"

const STATUS_OPTIONS = [
  { value: "Approved", labelKey: "Approved" },
  { value: "Suspended", labelKey: "Suspended" },
  { value: "Rejected", labelKey: "Rejected" },
]

const REASON_REQUIRED = ["Suspended", "Rejected"]

const PartnerStatusModal = ({
  isOpen,
  toggle,
  currentStatus,
  partner,
  onConfirm,
  onViewPendingOrders,
  loading = false,
}) => {
  const { t } = useTranslation()
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || "Approved")
  const [reason, setReason] = useState("")
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0)
  const [loadingPendingCount, setLoadingPendingCount] = useState(false)

  const partnerId = partner?._id || partner?.id

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(currentStatus || "Approved")
      setReason("")
      setPendingOrdersCount(0)
    }
  }, [isOpen, currentStatus])

  useEffect(() => {
    if (!isOpen || selectedStatus !== "Suspended" || !partnerId) {
      setPendingOrdersCount(0)
      return
    }

    let cancelled = false
    const fetchPendingCount = async () => {
      setLoadingPendingCount(true)
      try {
        const response = await getRiderPendingOrdersCount(partnerId)
        if (!cancelled) {
          setPendingOrdersCount(response?.data?.count ?? 0)
        }
      } catch {
        if (!cancelled) setPendingOrdersCount(0)
      } finally {
        if (!cancelled) setLoadingPendingCount(false)
      }
    }

    fetchPendingCount()
    return () => {
      cancelled = true
    }
  }, [isOpen, selectedStatus, partnerId])

  const needsReason = REASON_REQUIRED.includes(selectedStatus)
  const hasBlockingPendingOrders =
    selectedStatus === "Suspended" && pendingOrdersCount > 0

  const handleConfirm = () => {
    onConfirm(selectedStatus, reason)
    setReason("")
  }

  const handleClose = () => {
    setReason("")
    setSelectedStatus(currentStatus || "Approved")
    toggle()
  }

  const handleViewPendingOrders = () => {
    if (!partner || !onViewPendingOrders) return
    onViewPendingOrders(partner)
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      backdrop={loading ? "static" : true}
      keyboard={!loading}
    >
      <ModalHeader toggle={loading ? undefined : handleClose}>{t("Update Rider Status")}</ModalHeader>
      <ModalBody>
        <FormGroup tag="fieldset">
          <Label className="fw-semibold mb-2">{t("Select New Status")}</Label>
          {STATUS_OPTIONS.map(opt => (
            <div key={opt.value} className="form-check mb-2">
              <Input
                type="radio"
                className="form-check-input"
                id={`status-opt-${opt.value}`}
                name="partnerStatus"
                value={opt.value}
                checked={selectedStatus === opt.value}
                disabled={loading}
                onChange={() => setSelectedStatus(opt.value)}
              />
              <Label className="form-check-label" htmlFor={`status-opt-${opt.value}`}>
                {t(opt.labelKey)}
              </Label>
            </div>
          ))}
        </FormGroup>

        {selectedStatus === "Suspended" && (
          <div className="alert alert-warning py-2 px-3 mb-3">
            {loadingPendingCount ? (
              <span>{t("Checking assigned orders...")}</span>
            ) : pendingOrdersCount > 0 ? (
              <span>
                {t("This rider has")}{" "}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline fw-semibold"
                  onClick={handleViewPendingOrders}
                >
                  {t("{{count}} undelivered order(s)", { count: pendingOrdersCount })}
                </button>{" "}
                {t("assigned. Reassign them to another rider before suspending.")}
              </span>
            ) : (
              <span>{t("No undelivered orders are assigned to this rider.")}</span>
            )}
          </div>
        )}

        {needsReason && (
          <FormGroup>
            <Label>
              {t("Reason")} <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows={3}
              value={reason}
              disabled={loading}
              onChange={e => setReason(e.target.value)}
              placeholder={t("Enter reason for {{status}}...", {
                status: t(selectedStatus).toLowerCase(),
              })}
            />
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={
            loading ||
            (needsReason && !reason.trim()) ||
            hasBlockingPendingOrders ||
            loadingPendingCount
          }
        >
          {loading ? t("Saving...") : t("Confirm")}
        </Button>
        <Button color="light" onClick={handleClose} disabled={loading}>
          {t("Cancel")}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default PartnerStatusModal
